import {
  type EntityKind,
  type EntitySummary,
  formatFieldValue,
  groupsForType,
  isEntityKind,
  isReviewStatus,
  REVIEW_STATUSES,
  type ReviewStatus,
  type SchemaSnapshot,
  typeChain,
} from '@hoopoe/shared';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { FolderSearch, Plus, SearchX, X } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { DataNumber, DataText } from '../components/data/data-text';
import { EntityLink, REVIEW_STATUS_LABELS, StatusBadge } from '../components/entities/entity-link';
import { EmptyState } from '../components/guidance/empty-state';
import { ErrorState } from '../components/guidance/error-state';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { DIRECTORY_FIELDS, DIRECTORY_TABS, type ListResult, typeLabelOf, useDirectory, useFacets } from '../lib/entities';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';

// Directory (PLAN §12.7 screen 5): tabs per kind, facet filters, color dots and status borders. Everything is
// read through the claims API; Camp and Investigation status are claims, filtered on the materialized values.

export interface DirectorySearch {
  kind?: EntityKind;
  q?: string;
  type?: string;
  status?: ReviewStatus;
  camp?: string;
  inv?: string;
  tag?: string;
}

const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined);

export const Route = createFileRoute('/directory')({
  validateSearch: (s: Record<string, unknown>): DirectorySearch => ({
    kind: isEntityKind(s.kind) ? s.kind : undefined,
    q: str(s.q),
    type: str(s.type),
    status: isReviewStatus(s.status) ? s.status : undefined,
    camp: str(s.camp),
    inv: str(s.inv),
    tag: str(s.tag),
  }),
  component: DirectoryPage,
});

const TOUR = {
  id: 'directory',
  steps: [
    {
      target: '[data-tour="kinds"]',
      title: 'One tab per kind',
      body: 'Actors, events, places, assets, sources and cases. The number is how many match your filters.',
      learnMore: 'directory',
    },
    {
      target: '[data-tour="filters"]',
      title: 'Filters',
      body: 'Search any name in any script, or narrow by type, review status, Camp and investigation status.',
    },
    {
      target: '[data-tour="results"]',
      title: 'Colors and borders',
      body: 'The dot’s color is the entity’s type, its shape the kind. A solid outline is verified, dashed is unreviewed, double with ⚠ is disputed.',
      learnMore: 'colors-and-shapes',
    },
  ],
};

const FIELD_PARAM: Record<(typeof DIRECTORY_FIELDS)[number], 'camp' | 'inv'> = { camp: 'camp', investigation_status: 'inv' };

/** Does any type of this kind carry the field (e.g. Camp only exists on actors)? */
function kindHasField(snap: SchemaSnapshot, kind: EntityKind, key: string) {
  return snap.types.some(t => t.kind === kind && !t.archived && groupsForType(snap, t.id).some(g => g.fields.some(f => f.key === key)));
}

function apiParams(s: DirectorySearch, kind: EntityKind, fields: string[]) {
  const p = new URLSearchParams({ kind });
  if (s.q) p.set('q', s.q);
  if (s.type) p.set('type', s.type);
  if (s.status) p.set('status', s.status);
  if (s.tag) p.set('tag', s.tag);
  if (s.camp && fields.includes('camp')) p.set('f.camp', s.camp);
  if (s.inv && fields.includes('investigation_status')) p.set('f.investigation_status', s.inv);
  return p;
}

const dateOf = (ms: number) => new Date(ms).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

function DirectoryPage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const search = Route.useSearch();
  const kind = search.kind ?? 'actor';
  const tab = DIRECTORY_TABS.find(t => t.kind === kind) ?? DIRECTORY_TABS[0];
  const canWrite = useCan('content.write');
  const fields = useMemo(() => (snap ? DIRECTORY_FIELDS.filter(f => kindHasField(snap, kind, f)) : []), [snap, kind]);
  const params = apiParams(search, kind, fields);
  const listParams = new URLSearchParams(params);
  listParams.set('limit', '50');
  if (fields.length) listParams.set('with', fields.join(','));
  const facetParams = new URLSearchParams(params);
  if (fields.length) facetParams.set('facets', fields.join(','));
  const facets = useFacets(facetParams);
  const list = useDirectory(listParams);
  const [more, setMore] = useState<EntitySummary[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const listKey = listParams.toString();
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset extra pages when the filters change
  useEffect(() => {
    setMore([]);
    setNext(null);
  }, [listKey]);
  const cursor = more.length ? next : (list.data?.next ?? null);
  const rows = [...(list.data?.entities ?? []), ...more];
  const filtered = !!(search.q || search.type || search.status || search.camp || search.inv || search.tag);

  const loadMore = async () => {
    if (!cursor) return;
    const p = new URLSearchParams(listParams);
    p.set('after', cursor);
    const res = await api<ListResult>(`/entities?${p}`);
    setMore(m => [...m, ...res.entities]);
    setNext(res.next);
  };

  return (
    <>
      <PageHeader
        title="Directory"
        description="Every actor, event, place, asset, source and case, with what is known about it."
        actions={
          canWrite && (
            <Button size="sm" asChild>
              <Link to="/entity/new" search={{ kind }}>
                <Plus aria-hidden="true" /> New {tab?.singular}
              </Link>
            </Button>
          )
        }
      />
      <Tip id="directory" learnMore="directory">
        Values you type yourself are saved as verified, with you as their source. Open an entity to see each value’s review status and where
        it came from.
      </Tip>
      <nav aria-label="Entity kinds" data-tour="kinds" className="mt-4 flex flex-wrap gap-1 border-b">
        {DIRECTORY_TABS.map(t => (
          <Link
            key={t.kind}
            to="/directory"
            search={{ kind: t.kind, q: search.q }}
            aria-current={t.kind === kind ? 'page' : undefined}
            className="-mb-px flex items-center gap-2 border-b-2 border-transparent px-3 py-2 text-sm text-muted-foreground hover:text-foreground aria-[current=page]:border-foreground aria-[current=page]:font-medium aria-[current=page]:text-foreground"
          >
            {t.label}
            <DataNumber className="rounded-full bg-muted px-1.5 text-xs">{facets.data?.kinds[t.kind] ?? 0}</DataNumber>
          </Link>
        ))}
      </nav>
      {snap && <Filters snap={snap} kind={kind} search={search} fields={fields} facets={facets.data} />}
      <section aria-label={`${tab?.label} list`} data-tour="results" className="mt-4">
        {list.isError ? (
          <ErrorState error={list.error} title="The directory couldn’t be loaded" onRetry={() => list.refetch()} compact />
        ) : !snap || (list.isPending && !list.data) ? (
          <div className="space-y-2" aria-busy="true">
            <span className="sr-only">Loading…</span>
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          filtered ? (
            <EmptyState icon={SearchX} title={`No ${tab?.label.toLowerCase()} match these filters`} learnMore="directory">
              Try another spelling or script, or clear the filters.
              <div className="mt-3">
                <Button size="sm" variant="outline" asChild>
                  <Link to="/directory" search={{ kind }}>
                    Clear filters
                  </Link>
                </Button>
              </div>
            </EmptyState>
          ) : (
            <EmptyState
              icon={FolderSearch}
              title={`No ${tab?.label.toLowerCase()} yet`}
              learnMore="directory"
              action={
                canWrite && (
                  <Button size="sm" asChild>
                    <Link to="/entity/new" search={{ kind }}>
                      <Plus aria-hidden="true" /> New {tab?.singular}
                    </Link>
                  </Button>
                )
              }
            >
              {canWrite
                ? `Add one by hand, or upload documents: extracted ${tab?.label.toLowerCase()} appear here after review.`
                : `${tab?.label} appear here once documents are uploaded and reviewed.`}
            </EmptyState>
          )
        ) : (
          <ResultsTable snap={snap} rows={rows} fields={fields} caption={tab?.label ?? ''} />
        )}
        {cursor && (
          <div className="mt-3 flex justify-center">
            <Button size="sm" variant="outline" onClick={loadMore}>
              Show more
            </Button>
          </div>
        )}
      </section>
    </>
  );
}

function FacetSelect({
  label,
  help,
  value,
  onChange,
  options,
  allLabel,
}: {
  label: string;
  help: string;
  value: string;
  onChange: (v: string | undefined) => void;
  options: { value: string; label: string; count?: number }[];
  allLabel: string;
}) {
  const id = useId();
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1">
        <label htmlFor={id} className="text-xs font-medium">
          {label}
        </label>
        <FieldHelp label={label} help={help} learnMore="directory" />
      </div>
      <select
        id={id}
        value={value}
        onChange={e => onChange(e.target.value || undefined)}
        className="h-9 w-full rounded-md border border-input bg-surface px-2 text-sm"
      >
        <option value="">{allLabel}</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}
            {o.count !== undefined ? ` (${o.count})` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

function Filters({
  snap,
  kind,
  search,
  fields,
  facets,
}: {
  snap: SchemaSnapshot;
  kind: EntityKind;
  search: DirectorySearch;
  fields: string[];
  facets?: {
    statuses: Partial<Record<ReviewStatus, number>>;
    types: Record<string, number>;
    fields: Record<string, Record<string, number>>;
  };
}) {
  const navigate = useNavigate({ from: '/directory' });
  const set = (patch: Partial<DirectorySearch>) => navigate({ search: s => ({ ...s, ...patch }), replace: true });
  const [q, setQ] = useState(search.q ?? '');
  const qid = useId();
  // Follow the URL when it changes elsewhere (tabs, clear).
  useEffect(() => setQ(search.q ?? ''), [search.q]);
  useEffect(() => {
    if ((search.q ?? '') === q.trim()) return;
    const t = setTimeout(() => set({ q: q.trim() || undefined }), 250);
    return () => clearTimeout(t);
  });
  const types = snap.types
    .filter(t => t.kind === kind && !t.archived && t.parentTypeId)
    .map(t => ({
      value: t.key,
      label: typeChain(snap.types, t.id)
        .reverse()
        .slice(1)
        .map(x => x.label)
        .join(' › '),
      count: facets?.types[t.key] ?? 0,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const listOf = (key: string) => {
    const def = snap.fields.find(f => f.key === key);
    return snap.lists.find(l => l.key === def?.enumList);
  };
  const any = !!(search.q || search.type || search.status || search.camp || search.inv || search.tag);
  return (
    <form
      aria-label="Filter the directory"
      data-tour="filters"
      className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(14rem,2fr)_repeat(4,minmax(9rem,1fr))_auto]"
      onSubmit={e => {
        e.preventDefault();
        set({ q: q.trim() || undefined });
      }}
    >
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label htmlFor={qid} className="text-xs font-medium">
            Name or ID code
          </label>
          <FieldHelp
            label="Name or ID code"
            help="Finds any name, alias or transliteration in any script, ignoring accents and Arabic spelling variants, or part of an ID code."
            example="Hasan, حسن or MED_"
            learnMore="directory"
          />
        </div>
        <input
          id={qid}
          type="search"
          dir="auto"
          value={q}
          placeholder="e.g. Hasan, حسن or MED_"
          onChange={e => setQ(e.target.value)}
          className="h-9 w-full rounded-md border border-input bg-surface px-3 text-sm placeholder:text-muted-foreground"
        />
      </div>
      {types.length > 0 && (
        <FacetSelect
          label="Type"
          help="Sub-types are included: Organization also lists media, companies and NGOs."
          value={search.type ?? ''}
          onChange={type => set({ type })}
          options={types}
          allLabel="All types"
        />
      )}
      <FacetSelect
        label="Review status"
        help="Verified: typed or checked by a person. Unreviewed: from AI or an import, not yet checked. Disputed: sources disagree."
        value={search.status ?? ''}
        onChange={v => set({ status: v as ReviewStatus | undefined })}
        options={REVIEW_STATUSES.map(s => ({ value: s, label: REVIEW_STATUS_LABELS[s], count: facets?.statuses[s] ?? 0 }))}
        allLabel="Any status"
      />
      {fields.map(key => {
        const list = listOf(key);
        const param = FIELD_PARAM[key as (typeof DIRECTORY_FIELDS)[number]];
        const label = snap.fields.find(f => f.key === key)?.label ?? key;
        return (
          <FacetSelect
            key={key}
            label={label}
            help={snap.fields.find(f => f.key === key)?.help ?? `Entities whose ${label} is this value.`}
            value={search[param] ?? ''}
            onChange={v => set({ [param]: v })}
            options={(list?.values ?? [])
              .filter(v => !v.archived)
              .map(v => ({ value: v.key, label: v.label, count: facets?.fields[key]?.[v.key] ?? 0 }))}
            allLabel={`Any ${label.toLowerCase()}`}
          />
        );
      })}
      <div className="flex items-end">
        {any && (
          <Button type="button" size="sm" variant="ghost" onClick={() => navigate({ search: { kind }, replace: true })}>
            <X aria-hidden="true" /> Clear
          </Button>
        )}
      </div>
      {search.tag && (
        <p className="text-xs text-muted-foreground sm:col-span-2">
          Tagged <DataText className="font-medium text-foreground">{search.tag}</DataText>
        </p>
      )}
    </form>
  );
}

function ResultsTable({ snap, rows, fields, caption }: { snap: SchemaSnapshot; rows: EntitySummary[]; fields: string[]; caption: string }) {
  const defs = fields.map(k => snap.fields.find(f => f.key === k)).filter(f => !!f);
  return (
    <div className="relative overflow-x-auto rounded-lg border bg-surface">
      <table className="w-full text-sm">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr className="border-b bg-muted text-start text-xs">
            <th scope="col" className="p-2 text-start font-medium">
              Name
            </th>
            <th scope="col" className="p-2 text-start font-medium">
              ID code
            </th>
            <th scope="col" className="p-2 text-start font-medium">
              Type
            </th>
            {defs.map(d => (
              <th key={d.key} scope="col" className="p-2 text-start font-medium">
                {d.label}
              </th>
            ))}
            <th scope="col" className="p-2 text-start font-medium">
              Review status
            </th>
            <th scope="col" className="p-2 text-start font-medium">
              Updated
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(e => (
            <tr key={e.id} className="border-b last:border-0" data-entity-id={e.id}>
              <td className="max-w-80 p-2">
                <EntityLink schema={snap} entity={e} />
                {e.matchedName && (
                  <span className="ms-6 block truncate text-xs text-muted-foreground">
                    matched <DataText>{e.matchedName}</DataText>
                  </span>
                )}
              </td>
              <td className="p-2">
                <code className="text-xs">{e.code}</code>
              </td>
              <td className="p-2 text-muted-foreground">{typeLabelOf(snap, e.typeId)}</td>
              {defs.map(d => (
                <td key={d.key} className="p-2">
                  {(e.fieldValues?.[d.key] ?? []).map(v => formatFieldValue(d, v, snap)).join(', ') || (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              ))}
              <td className="p-2">
                <StatusBadge status={e.reviewStatus} />
              </td>
              <td className="p-2 text-muted-foreground">
                <DataNumber>{dateOf(e.updatedAt)}</DataNumber>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
